import React, { useState } from 'react';
import { caseStudies } from '../data/mock';
import { ArrowRight, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const CaseStudies = () => {
  const [selectedCase, setSelectedCase] = useState(null);

  return (
    <section id="case-studies" className="py-16 bg-[#0B0F14] relative">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-10 space-y-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-white font-['Qurova',sans-serif]">
            Success <span className="text-[#3DF2E0]">Stories</span>
          </h2>
          <p className="text-xl text-[#9CA3AF] font-['League_Spartan'] max-w-2xl mx-auto">
            Real results from AI implementations across different industries
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {caseStudies.map((caseStudy) => (
            <div
              key={caseStudy.id}
              onClick={() => setSelectedCase(caseStudy)}
              className="group bg-[#0B0F14] border border-[#1F2933] rounded-md p-8 hover:border-[#3DF2E0] transition-all duration-300 cursor-pointer"
              style={{
                boxShadow: '0 0 0 rgba(61, 242, 224, 0)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 15px rgba(61, 242, 224, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 0 rgba(61, 242, 224, 0)';
              }}
            >
              {/* Industry Badge */}
              <div className="inline-block mb-4">
                <span className="text-xs font-['JetBrains_Mono',monospace] text-[#3DF2E0] bg-[#3DF2E0]/10 px-3 py-1 rounded border border-[#3DF2E0]/30">
                  {caseStudy.industry}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-semibold text-white font-['Qurova',sans-serif] mb-3 group-hover:text-[#3DF2E0] transition-colors duration-300">
                {caseStudy.title}
              </h3>

              {/* Description */}
              <p className="text-lg text-[#9CA3AF] font-['League_Spartan'] mb-6 leading-relaxed">
                {caseStudy.description}
              </p>

              {/* Key Results */}
              <div className="space-y-2 mb-6">
                {caseStudy.results.slice(0, 2).map((result, index) => (
                  <div key={index} className="text-lg flex items-center text-sm text-[#9CA3AF] font-['League_Spartan']">
                    <div className="w-1 h-1 bg-[#3DF2E0] rounded-full mr-2" />
                    {result}
                  </div>
                ))}
              </div>

              {/* View Details */}
              <div className="text-lg flex items-center text-[#3DF2E0] font-['League_Spartan'] text-sm group-hover:translate-x-2 transition-transform duration-300">
                View Details
                <ArrowRight className="w-4 h-4 ml-2" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Case Study Modal */}
      <Dialog open={!!selectedCase} onOpenChange={() => setSelectedCase(null)}>
        <DialogContent
          className="bg-[#0B0F14] border border-[#3DF2E0] text-white 
  max-w-2xl 
  w-[92vw] 
  max-h-[90vh] 
  overflow-y-auto 
  p-6
  rounded-lg"
          aria-describedby="case-study-description"
        >
          {selectedCase && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-['Qurova',sans-serif] text-[#3DF2E0]">
                  {selectedCase.title}
                </DialogTitle>
              </DialogHeader>
              <div id="case-study-description" className="space-y-6 mt-4 pb-6">
                <div>
                  <span className="text-xs font-['JetBrains_Mono',monospace] text-[#3DF2E0] bg-[#3DF2E0]/10 px-3 py-1 rounded border border-[#3DF2E0]/30">
                    {selectedCase.industry}
                  </span>
                </div>
                <p className="text-lg text-[#9CA3AF] font-['League_Spartan'] leading-relaxed">
                  {selectedCase.description}
                </p>

                <div className="border-t border-[#1F2933] pt-6">
                  <h4 className="text-lg font-semibold text-white font-['Qurova',sans-serif] mb-4">
                    Key Results
                  </h4>
                  <div className="space-y-3">
                    {selectedCase.results.map((result, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-[#3DF2E0] rounded-full mr-3 mt-1.5" />
                        <span className="text-lg text-[#9CA3AF] font-['League_Spartan']">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#1F2933] pt-6">
                  <h4 className="text-lg font-semibold text-white font-['Qurova',sans-serif] mb-4">
                    Technologies Used
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCase.tech.map((tech, index) => (
                      <span
                        key={index}
                        className="text-xs font-['JetBrains_Mono',monospace] text-white bg-[#1F2933] px-3 py-1.5 rounded border border-[#1F2933]"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CaseStudies;
